import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import { Anchor } from '@twilio-paste/anchor';
import { Box } from '@twilio-paste/box';
import { Paragraph } from '@twilio-paste/paragraph';
import { DoDont, Do, Dont } from '../../../components/DoDont';
import { Codeblock } from '../../../components/codeblock';
import { SidebarCategoryRoutes } from '../../../constants';
import Changelog from '@twilio-paste/paragraph/CHANGELOG.md';
export const pageQuery = graphql`
  {
    allPasteComponent(filter: {name: {eq: "@twilio-paste/paragraph"}}) {
      edges {
        node {
          name
          description
          status
          version
        }
      }
    }
    mdx(fields: {slug: {eq: "/components/paragraph/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const ComponentHeader = makeShortcode("ComponentHeader");
const PageAside = makeShortcode("PageAside");
const LivePreview = makeShortcode("LivePreview");
const ChangelogRevealer = makeShortcode("ChangelogRevealer");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <ComponentHeader name={props.pageContext.frontmatter.title} categoryRoute={SidebarCategoryRoutes.COMPONENTS} githubUrl="https://github.com/twilio-labs/paste/tree/main/packages/paste-core/components/paragraph" storybookUrl="/?path=/story/components-paragraph--default" abstractUrl="https://share.goabstract.com/85c81258-90b9-49c8-b34b-9418da5f2f00" data={props.data.allPasteComponent.edges} mdxType="ComponentHeader" />
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <LivePreview scope={{
          Paragraph
        }} language="jsx" mdxType="LivePreview">
  {`<Paragraph>
  Impossible is just a big word thrown around by small men who find it easier to live in the world they&rsquo;ve been given than to explore the power they have to change it. Impossible is not a fact. It&rsquo;s an opinion. Impossible is not a declaration. It's a dare. Impossible is potential. Impossible is temporary. Impossible is nothing.
</Paragraph>`}
        </LivePreview>
        <h2>{`Guidelines`}</h2>
        <p>{`By default, the paragraph component should provide consistent values for font-size, line-height, and margin-bottom across themes.`}</p>
        <h3>{`About Paragraphs`}</h3>
        <p>{`Paragraphs are distinct sections of content, typically dealing with a single theme, and are indicated by a new line. Paragraphs are often used for a majority of text on the page and should be able to support a variety of information-dense layouts.`}</p>
        <h3>{`Accessibility`}</h3>
        <p>{`Paragraph text should be legible, and easy to read. It should also provide a clear amount of space beneath a block of text to visually separate itself from adjacent content.`}</p>
        <ul>
          <li parentName="ul">{`Breaking content up into paragraphs helps screen readers and assistive technology provide shortcuts for users to skip between them`}</li>
          <li parentName="ul">{`Paragraph text should meet AA requirements (4.5:1) for color contrast from itself and the background color`}</li>
        </ul>
        <h2>{`Examples`}</h2>
        <h3>{`Default Paragraph`}</h3>
        <LivePreview scope={{
          Paragraph
        }} language="jsx" mdxType="LivePreview">
  {`<article>
  <Paragraph>
  We have inherited a fear of memories of slavery. It is as if to remember and acknowledge slavery would amount to our being consumed by it. As a matter of fact, in the popular black imagination, it is easier for us to construct ourselves as children of Africa, as the sons and daughters of kings and queens, and thereby ignore the Middle Passage and centuries of enforced servitude in the Americas. Although some of us might indeed be the descendants of African royalty, most of us are probably descendants of their subjects, the daughters and sons of African peasants or workers.
</Paragraph>
</article>`}
        </LivePreview>
        <h2>{`Composition Notes`}</h2>
        <h3>{`When to Use a Paragraph`}</h3>
        <p>{`Paragraphs should be used for most text blocks. Paragraphs provide defaults for font-size, line-height, and margin-bottom. Paragraphs can be used within other components as well. Typically, these are anywhere from 1-5 sentences. Use paragraphs like you would when authoring a word document, to group sentences of a similar theme.`}</p>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use paragraphs for text blocks." center mdxType="Do">
    <Codeblock mdxType="Codeblock">
      {`<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
  sit amet non magna. Nullam quis risus eget urna mollis ornare vel eu leo.
</Paragraph>
<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
  sit amet non magna. Nullam quis risus eget urna mollis ornare vel eu leo.
</Paragraph>`}
    </Codeblock>
  </Do>
  <Dont title="Don't" body="Don’t use paragraphs for spacing or when more semantic elements can be used in their place, such as headings, lists, or labels." mdxType="Dont">
    <Codeblock mdxType="Codeblock">
      {`<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
</Paragraph>
<Paragraph />
<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
</Paragraph>`}
    </Codeblock>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Break content up into small paragraphs that allow users to skim the information." center mdxType="Do">
    <Codeblock mdxType="Codeblock">{`<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur. Maecenas faucibus
  mollis interdum. Maecenas sed diam eget risus varius blandit sit amet non magna.
</Paragraph>
<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur. Maecenas faucibus
  mollis interdum. Maecenas sed diam eget risus varius blandit sit amet non magna.
</Paragraph>`}
    </Codeblock>
  </Do>
  <Dont title="Don't" body="Don’t create overly long paragraphs that make it difficult for the reader to find information." mdxType="Dont">
    <Codeblock mdxType="Codeblock">{`<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
  sit amet non magna. Nullam quis risus eget urna mollis ornare vel eu leo.
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
  sit amet non magna. Nullam quis risus eget urna mollis ornare vel eu leo.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
  sit amet non magna. Nullam quis risus eget urna mollis ornare vel eu leo.
</Paragraph>`}
    </Codeblock>
  </Dont>
        </DoDont>
        <DoDont mdxType="DoDont">
  <Do title="Do" body="Use paragraphs as sibling elements, with only inline elements such as bold or italic as children." center mdxType="Do">
    <Codeblock mdxType="Codeblock">
      {`<Paragraph>
  Donec sed odio dui. <a href="#">Aenean lacinia</a> bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
</Paragraph>
<Paragraph marginBottom="space0">
  Donec sed odio dui. <strong>Aenean lacinia bibendum</strong> nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
</Paragraph>`}
    </Codeblock>
  </Do>
  <Dont title="Don't" body="Don’t nest paragraphs within each other either, only inline elements should be nested within a paragraph." mdxType="Dont">
    <Codeblock mdxType="Codeblock">
      {`<Paragraph>
  Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
  Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
  <Paragraph marginBottom="space0">
    Donec sed odio dui. Aenean lacinia bibendum nulla sed consectetur.
    Maecenas faucibus mollis interdum. Maecenas sed diam eget risus varius blandit
  </Paragraph>
</Paragraph>`}
    </Codeblock>
  </Dont>
        </DoDont>
        <h2>{`Anatomy`}</h2>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Property`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default token`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Modifiable?`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`font-size`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`font-size-30`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`line-height`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`line-height-30`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`font-weight`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`font-weight-normal`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`margin-bottom`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`space-70`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Yes`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`color`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`color-text`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`No`}</td>
            </tr>
          </tbody>
        </table>
        <hr></hr>
        <h2>{`Usage Guide`}</h2>
        <h3>{`API`}</h3>
        <h4>{`Installation`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`yarn add @twilio-paste/paragraph - or - yarn add @twilio-paste/core
`}</code></pre>
        <h4>{`Usage`}</h4>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`import {Paragraph} from '@twilio-paste/paragraph';

const Component = () => (
  <Paragraph>
    We have inherited a fear of memories of slavery. It is as if to remember and acknowledge slavery would amount to our
    being consumed by it. As a matter of fact, in the popular black imagination, it is easier for us to construct
    ourselves as children of Africa, as the sons and daughters of kings and queens, and thereby ignore the Middle
    Passage and centuries of enforced servitude in the Americas. Although some of us might indeed be the descendants of
    African royalty, most of us are probably descendants of their subjects, the daughters and sons of African peasants
    or workers.
  </Paragraph>
);
`}</code></pre>
        <h4>{`Props`}</h4>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Prop`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Type`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Default`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`marginBottom`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`'space0'`}</td>
              <td parentName="tr" {...{
                "align": null
              }}></td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`children?`}</td>
              <td parentName="tr" {...{
                "align": null
              }}><inlineCode parentName="td">{`ReactNode`}</inlineCode></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`null`}</td>
            </tr>
          </tbody>
        </table>
        <h5>{`marginBottom prop`}</h5>
        <p>{`Only allows passing 'space0' to unset the bottom margin. Useful
when using Paragraph as the last child of a wrapping component, like
a Card.`}</p>
        <ChangelogRevealer mdxType="ChangelogRevealer">
  <Changelog mdxType="Changelog" />
        </ChangelogRevealer>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      